import { useEffect, useState, useMemo } from "react";
import { auth } from "../../backend/firebase";
import { onAuthStateChanged } from "firebase/auth"; // Imported for Auth listener
import {
  getFirestore,
  collection,
  query,
  onSnapshot,
  orderBy,
  doc, // Imported for document reference
  setDoc, // Imported for writing data
} from "firebase/firestore";
import "./Home.css";
import Dataset from "../../components/Dataset";
import { useNavigate } from "react-router-dom";

const db = getFirestore();

// 1. HARDCODED CATEGORIES LIST
const CATEGORIES_LIST = [
  "All",
  "Economy & Finance",
  "Health",
  "Agriculture",
  "Technology",
  "Environment & Climate",
  "Education",
  "Transport & Mobility",
  "Government & Public Policy",
  "Population & Demographics",
  "Science & Research",
  "Energy",
  "Business & Industry",
  "Crime & Safety",
  "Geography & Mapping",
  "Sports",
  "Culture & Media",
];

// Helper to format numbers (e.g. 1200 -> 1.2k)
const formatNumber = (num) => {
  if (!num) return "0";
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "k";
  return num.toString();
};

// Helper for dates
const timeAgo = (dateString) => {
  if (!dateString) return "Unknown";
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + "y ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + "mo ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + "d ago";
  return "Just now";
};

export default function Home() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const navigate = useNavigate();

  // ---------------------------------------------------------
  // 1. NEW LOGIC: Handle User Saving on Page Load
  // ---------------------------------------------------------
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is logged in via Google
        try {
          const userRef = doc(db, "Users", user.uid);

          // Save/Update user info using merge: true to avoid overwriting existing fields
          await setDoc(
            userRef,
            {
              displayName: user.displayName,
              email: user.email,
              profile_pic: user.photoURL, // Maps Google photo to your field name
              uid: user.uid,
              lastLogin: new Date().toISOString(), // Optional: Track when they last visited
            },
            { merge: true }
          );

          console.log("User profile synced to Firestore");
        } catch (error) {
          console.error("Error saving user profile:", error);
        }
      } else {
        console.log("No user logged in");
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribeAuth();
  }, []);

  // ---------------------------------------------------------
  // 2. EXISTING LOGIC: Fetch Datasets
  // ---------------------------------------------------------
  useEffect(() => {
    const q = query(collection(db, "datasets"), orderBy("lastUpdated", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDatasets(docs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter Logic
  const filteredDatasets = useMemo(() => {
    return datasets.filter((ds) => {
      const matchesSearch =
        ds.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ds.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        selectedCategory === "All" || ds.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [datasets, searchQuery, selectedCategory]);

  return (
    <div className="home-wrapper">
      {/* BACKGROUND MESH */}
      <div className="mesh-background">
        <div className="mesh-blob blob-1"></div>
        <div className="mesh-blob blob-2"></div>
      </div>

      {/* MAIN DASHBOARD LAYOUT */}
      <main className="dashboard-container">
        {/* SIDEBAR FILTERS */}
        <aside className="filter-sidebar">
          <div className="filter-group">
            <h3>Categories</h3>
            <div className="filter-list">
              {CATEGORIES_LIST.map((cat) => (
                <button
                  key={cat}
                  className={`filter-btn ${
                    selectedCategory === cat ? "active" : ""
                  }`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Trending</h3>
            <div className="stat-row">
              <span className="trend-icon">🔥</span>
              <span>Most Downloaded</span>
            </div>
            <div className="stat-row">
              <span className="trend-icon">⭐</span>
              <span>Highest Rated</span>
            </div>
          </div>
        </aside>

        {/* DATASET GRID */}
        <section className="dataset-feed">
          <div className="feed-header">
            <h2>Explore Datasets</h2>
            <span
              className="count-badge"
              onClick={() => navigate(`/documentation`)}
            >
              Documentation{" "}
            </span>
          </div>

          {loading ? (
            <div className="loading-state">Loading repository...</div>
          ) : (
            <div className="grid-layout">
              {filteredDatasets.map((ds) => (
                <Dataset key={ds.id} datasetid={ds.id} />
              ))}

              {filteredDatasets.length === 0 && (
                <div className="empty-state">
                  <p>
                    No datasets found for <strong>{selectedCategory}</strong>.
                  </p>
                  <button
                    className="btn-outline"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("All");
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
